<template>
  <div
    ref="smsMessagesRef"
    class="flex-col-reverse space-y-4 overflow-y-auto px-4 pb-4"
    :class="{ scrolling: hasScrollBar }"
  >
    <loader :is-loading="isLoading || isFetching" />
    <div
      v-if="hasNextPage && !isFetchingNextPage && !isLoading"
      class="flex w-full items-center justify-center"
    >
      <button class="p-5" @click.stop.prevent="loadNextPage">
        <span class="text-xs text-primary">
          {{ $t("COMMON.LOAD_MORE") }} {{ $t("COMMON.MESSAGE", 2) }}
        </span>
      </button>
    </div>
    <div
      v-for="(conversation, date, dateIndex) in groupedConversation"
      :key="date"
      class="date"
    >
      <span class="text-center">
        <template v-if="isToday(new Date(date))">
          {{ $t("COMMON.DATES.TODAY") }}
        </template>
        <date-time
          v-else
          :date="new Date(date).toISOString()"
          is-fixed
          format="MMMM dd, yyyy"
        />
      </span>
      <thread-item-wrapper
        v-for="(sms, index) in conversation"
        :key="sms.id"
        data-cy="sms-thread-item"
        :disable-comments="
          dateIndex < numberOfDays - 1 || index !== conversation.length - 1
        "
        :comments="log?.data?.comments"
        is-sms
        :wrapper-class="[
          'sms-wrapper group/message p-3',
          sms.is_outgoing ? 'outgoing-sms' : 'incoming-sms',
        ]"
      >
        <template #content>
          <div class="flex flex-col space-y-1 text-gray-500">
            <div class="space-y-3">
              <div class="flex w-full space-x-3">
                <div
                  class="flex-1 whitespace-pre-wrap text-label"
                  data-cy="sms-message"
                >
                  {{ sms.message }}
                </div>
              </div>
            </div>
          </div>
        </template>
        <template #footer>
          <div v-if="sms" class="relative">
            <div class="flex w-full justify-end space-y-1">
              <div class="flex items-center space-x-2">
                <date-time
                  v-if="sms.created_at"
                  :date="new Date(sms.created_at).toISOString()"
                  is-fixed
                  format="hh:mm a"
                />
                <sms-status v-if="sms.status" :status="sms.status" />
                <links-clicked-tooltip-icon
                  v-if="!!sms.link_clicked_events?.length"
                  data-cy="sms-links-clicked"
                >
                  <div
                    class="flex flex-col space-y-1"
                    data-cy="sms-links-clicked-detail"
                  >
                    <div
                      v-for="datetime in sms.link_clicked_events"
                      :key="datetime"
                      class="max-w-200 truncate text-xxs text-white/80"
                    >
                      <span class="text-white">
                        {{
                          formatDateCustom(
                            datetime,
                            "MMM dd, yyyy hh:mm a",
                            true
                          )
                        }}
                      </span>
                    </div>
                  </div>
                </links-clicked-tooltip-icon>
              </div>
            </div>
            <div
              class="absolute z-10 bg-white rounded-md top-full mt-2 -right-3 p-1 transition-opacity group-hover/message:opacity-100 flex items-stretch"
              :class="{ 'opacity-0': activeSmsLog?.id !== sms.id }"
            >
              <div class="p-0-25">
                <div class="rounded border flex space-x-1 items-stretch p-1">
                  <div class="action-item" @click="copyContent(sms)">
                    <icon-base
                      :icon="IconCopy"
                      width="16"
                      height="16"
                      view-box="0 0 20 20"
                    />
                  </div>
                  <div
                    v-tooltip="$t('INBOX.OPEN_COMMENTS')"
                    class="action-item"
                    @click="toggleComments(sms)"
                  >
                    <icon-base :icon="IconComment" />
                    <div
                      class="pill flex w-auto translate-y-0-25 transform items-center justify-center rounded-full px-1-5 text-center text-xs font-semibold"
                      :class="
                        sms.comments?.length
                          ? 'bg-primary text-white'
                          : 'bg-gray-200 text-gray-600'
                      "
                    >
                      {{ sms.comments?.length ?? "0" }}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </template>
        <template #custom>
          <div
            v-if="sms.status_message"
            class="flex space-x-2 error"
            :class="sms.is_outgoing ? 'outgoing-error' : 'incoming-error'"
          >
            <icon-base
              :icon="IconErrorWarning"
              width="12"
              height="12"
              view-box="0 0 16 16"
              class="text-error shrink-0 mt-1"
            />
            <span class="text-error text-xs">{{ sms.status_message }}</span>
          </div>
          <div
            v-if="activeSmsLog?.id === sms.id"
            class="comments"
            :class="sms.is_outgoing ? 'outgoing-comments' : 'incoming-comments'"
          >
            <div
              v-if="!sms.comments?.length"
              class="flex items-center justify-center"
            >
              <p>
                {{ $t("ACTIVITY.COMMENTS.EMPTY") }}
              </p>
            </div>
            <log-comment-form
              :log-id="sms.id"
              :sms-id="log?.data?.sms_conversation_id"
            />
            <log-comment
              v-for="comment in sms.comments"
              :key="comment.id"
              :data="comment"
              :sms-id="log?.data?.sms_conversation_id"
            />
          </div>
        </template>
      </thread-item-wrapper>
    </div>

    <div class="space-y-4">
      <log-quick-submit
        v-model="message"
        class="quick-submit-form relative"
        :placeholder="$t('ACTIVITY.SMS.MESSAGE_PLACEHOLDER')"
        :return-placeholder="$t('ACTIVITY.SMS.RETURN_PLACEHOLDER')"
        :loading="isSending"
        is-sms
        auto-resizing
        @submit="sendSms"
      />
    </div>
  </div>
</template>
<script lang="ts" setup>
import {
  type Ref,
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  provide,
  ref,
  toRef,
  useTemplateRef,
  watch,
} from "vue";

import { isToday } from "date-fns/isToday";
import groupBy from "lodash/groupBy";
import orderBy from "lodash/orderBy";
import { storeToRefs } from "pinia";

import { LogEndpointType } from "@/enums/communicationLogs";
import { formatDateCustom } from "@/helpers/formatting";
import { CommunicationLogContextKey } from "@/helpers/symbols";
import {
  leaveChannel,
  subscribeToChannelEvents,
} from "@/helpers/websockets/channels";
import { SMS_CHANNEL } from "@/helpers/websockets/common";
import { useClipboardWithMessage } from "@/hooks/clipboard";
import { useTwilio } from "@/hooks/communicationProviders";
import {
  useReplyToSmsMutation,
  useSmsConversationQuery,
} from "@/hooks/queries/useCommunication";
import useSmsStore from "@/stores/sms";

import LogComment from "@/components/communicationLogs/comments/LogComment.vue";
import LogCommentForm from "@/components/communicationLogs/comments/LogCommentForm.vue";
import LinksClickedTooltipIcon from "@/components/communicationLogs/communication/emailThread/LinksClickedTooltipIcon.vue";
import SmsStatus from "@/components/communicationLogs/communication/sms/SmsStatus.vue";
import ThreadItemWrapper from "@/components/communicationLogs/communication/threads/openState/ThreadItemWrapper.vue";
import LogQuickSubmit from "@/components/communicationLogs/forms/LogQuickSubmit.vue";
import IconComment from "@/components/icons/IconComment.vue";
import IconCopy from "@/components/icons/IconCopy.vue";
import IconErrorWarning from "@/components/icons/IconErrorWarning.vue";
import DateTime from "@/components/ui/ConvertibleDateTime.vue";
import IconBase from "@/components/ui/IconBase.vue";

import type { SmsLog, SmsLogData } from "@/models/communicationLogs";

interface Props {
  log: SmsLog;
}

const { log } = defineProps<Props>();

provide(CommunicationLogContextKey, {
  type: LogEndpointType.Sms,
  logId: log.data.id,
});

const { savedPhoneNumber } = useTwilio();
const { incomingSmsEvents } = storeToRefs(useSmsStore());
const message = ref("");
const initialLoadComplete = ref(false);
const activeSmsLog = ref<SmsLogData | null>(null);
const smsMessagesRef = useTemplateRef("smsMessagesRef");
const {
  data: conversationData,
  isLoading,
  status,
  isFetching,
  refetch,
  fetchNextPage,
  hasNextPage,
  isFetchingNextPage,
  // we have id check in the parent
} = useSmsConversationQuery(toRef(log.data.sms_conversation_id) as Ref<number>);
const { mutateAsync: replyToSms, isPending: isSending } =
  useReplyToSmsMutation();
const copyToClipboard = useClipboardWithMessage();

const allDataPages = computed(
  () =>
    conversationData.value?.pages?.flatMap(
      (paginatedResponse) => paginatedResponse.data
    ) ?? []
);

const groupedConversation = computed(() => {
  return groupBy(
    orderBy(allDataPages.value, (message) => new Date(message.created_at)),
    (message) => formatDateCustom(message.created_at, "MM/dd/yyyy", true)
  );
});
const numberOfDays = computed(
  () => Object.keys(groupedConversation.value).length
);
const hasScrollBar = computed(
  () =>
    smsMessagesRef.value &&
    smsMessagesRef.value.scrollHeight > smsMessagesRef.value?.clientHeight
);

const sendSms = async () => {
  // must reply to incoming message only
  const lastIncomingMessageId = allDataPages.value.find(
    (message) => !message.is_outgoing
  )?.id;
  if (!lastIncomingMessageId || !message.value) {
    return;
  }
  const from = savedPhoneNumber.value?.phone_number ?? "";
  await replyToSms({
    id: lastIncomingMessageId,
    message: message.value,
    from,
  });
  message.value = "";
};

const loadNextPage = () => {
  initialLoadComplete.value = true;
  fetchNextPage();
};

const scrollToBottom = () => {
  if (!smsMessagesRef.value) {
    return;
  }
  smsMessagesRef.value.scrollTop = smsMessagesRef.value.scrollHeight;
};

const copyContent = async (sms: SmsLogData) => {
  if (!sms.message) {
    return;
  }
  await copyToClipboard(sms.message);
};

const toggleComments = (sms: SmsLogData) => {
  if (activeSmsLog.value && activeSmsLog.value.id === sms.id) {
    activeSmsLog.value = null;
    return;
  }
  activeSmsLog.value = sms;
};

watch(
  status,
  async (newValue) => {
    if (newValue === "success" && !initialLoadComplete.value) {
      await nextTick();
      scrollToBottom();
    }
  },
  { immediate: true }
);

watch(
  incomingSmsEvents,
  async (newValue, oldValue) => {
    if (newValue === oldValue) {
      return;
    }
    const haveNewMessageForThisThread = newValue.some(
      (message) => message.sms_conversation_id === log.data.sms_conversation_id
    );
    if (haveNewMessageForThisThread) {
      initialLoadComplete.value = true;
      await refetch();
    }
  },
  { deep: true, immediate: true }
);

onMounted(async () => {
  await nextTick();
  scrollToBottom();
  if (log.data.sms_conversation_id) {
    subscribeToChannelEvents(SMS_CHANNEL);
  }
});

onBeforeUnmount(() => {
  leaveChannel(SMS_CHANNEL);
});
</script>

<style scoped>
@reference "@/assets/tailwind.css";
:deep(.quick-submit-form) {
  .action-button-wrapper {
    @apply w-full;
  }
}
:deep() {
  .date {
    @apply flex w-full flex-col gap-y-4;
  }

  .outgoing-sms {
    @apply bg-surface-user-blue;
  }
  .incoming-sms {
    @apply bg-grey-hover;
  }

  .sms-wrapper,
  .error {
    @apply w-3/4;
  }
  .error {
    @apply mt-2;
    + .comments {
      @apply mt-2;
    }
  }
  .comments {
    @apply mt-8 w-100 space-y-3 rounded border p-3;
  }
  .outgoing-sms,
  .outgoing-comments,
  .outgoing-error {
    @apply ml-auto;
  }
  .incoming-sms,
  .incoming-comments,
  .incoming-error {
    @apply mr-auto;
  }
}

.action-item {
  @apply flex h-6 cursor-pointer items-center space-x-2 rounded px-1 hover:bg-disabled;
}
</style>
